import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reportsApi } from '../../api/reports';
import type { GenerateReportInput, ReportScheduleInput } from '../../types/domain';

const KEY = ['reports'] as const;
const SCHEDULES_KEY = ['report-schedules'] as const;

/** Polls every 3s while any report in the list is still Pending/Running, so a just-triggered
 * export's status flips to Ready/Failed without the admin manually refreshing. */
export function useGeneratedReports() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => reportsApi.list(),
    refetchInterval: (query) => {
      const data = query.state.data;
      const stillWorking = data?.some((r) => r.status === 'Pending' || r.status === 'Running');
      return stillWorking ? 3000 : false;
    },
    // Without this, TanStack Query's interval tick silently no-ops whenever
    // focusManager.isFocused() is false (window/tab not focused at the OS level) — an admin who
    // triggers an export and switches tabs would otherwise never see it flip to Ready.
    refetchIntervalInBackground: true,
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateReportInput) => reportsApi.generate(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Downloads and triggers a browser save — same Blob-to-anchor pattern as lib/csv.ts's
 * downloadCsv, just with a server-fetched Blob instead of a client-built one. */
export function useDownloadReport() {
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      const blob = await reportsApi.download(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useReportSchedules() {
  return useQuery({ queryKey: SCHEDULES_KEY, queryFn: () => reportsApi.listSchedules() });
}

export function useCreateReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReportScheduleInput) => reportsApi.createSchedule(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useUpdateReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReportScheduleInput }) => reportsApi.updateSchedule(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useDeleteReportSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportsApi.deleteSchedule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}
